import { api } from './client'

// A bank statement CSV; date_order 'mdy' (10/14/2026) or 'dmy' (14/10/2026).
export const bankImportCsvText = async (csv_text, date_order = 'mdy') => {
  const { data } = await api.post('/accounting/bank/import', { csv_text, date_order })
  return data
}

// Book lines a statement line could be matched to, best first.
export const getBankCandidates = async (stmtId, cashCode) => (await api.get('/accounting/bank/candidates', { params: { stmt_id: stmtId, cash_code: cashCode } })).data

export const matchBankLine = async (stmtId, jlId) => (await api.post('/accounting/bank/match', { stmt_id: stmtId, jl_id: jlId })).data

export const unmatchBankLine = async (stmtId) => (await api.delete(`/accounting/bank/match/${stmtId}`)).data

export const deleteBankLine = async (stmtId) => (await api.delete(`/accounting/bank/statements/${stmtId}`)).data

export const bankAutoMatch = async ({ cash_code, date_tol_days, amount_tol } = {}) => {
  const { data } = await api.post('/accounting/bank/auto-match', { cash_code, date_tol_days, amount_tol })
  return data
}

export const getBankData = async ({ cash_code, tol_days } = {}) => {
  const params = {}
  if (cash_code) params.cash_code = cash_code
  if (tol_days) params.tol_days = tol_days
  const { data } = await api.get('/accounting/bank/data', { params })
  return data
}

export const reopenPeriod = async ({ period_ym }) => {
  const { data } = await api.post('/accounting/period/reopen', { period_ym })
  return data
}

export const closePeriodWithBalances = async ({ period_ym, note }) => {
  const { data } = await api.post('/accounting/period/close-with-balances', { period_ym, note })
  return data
}

// What the Accounting summary shows (see GET /accounting/summary).
export const getAccountingSummary = async () => (await api.get('/accounting/summary')).data

// Journal entries, one row each: [{je_id, je_no, date, memo, status, source, amount, lines, accounts}]
export const getJournal = async ({ start, end, status } = {}) => {
  const params = {}
  if (start) params.start = start
  if (end) params.end = end
  if (status) params.status = status
  return (await api.get('/accounting/journal', { params })).data
}

// {header, lines, source, period_closed, can_edit, can_void}
export const getJournalEntry = async (jeId) => (await api.get(`/accounting/journal-entries/${jeId}`)).data

// A manual entry with all its lines: {entry_date, memo, lines, post?}. New when jeId is empty.
export const saveJournalEntry = async (jeId, payload) => (jeId
  ? await api.put(`/accounting/journal-entries/${jeId}/full`, payload)
  : await api.post('/accounting/journal-entries/full', payload)).data

export const deleteJournalEntry = async (jeId) => (await api.delete(`/accounting/journal-entries/${jeId}`)).data

export const voidJournalEntry = async (jeId) => (await api.post(`/accounting/journal-entries/${jeId}/void`)).data

// Checks before closing a month, and the closed months (see GET /accounting/month-end).
export const getMonthEnd = async (periodYm) => (await api.get('/accounting/month-end', { params: periodYm ? { period_ym: periodYm } : {} })).data

// GAAP statements (backend financial_statements.py).
export const getIncomeStatementGaap = async ({ start, end, compare = 'previous' }) => (await api.get('/accounting/statements/income', { params: { start, end, compare } })).data
export const getBalanceSheetGaap = async ({ as_of, compare = 'year_end' }) => (await api.get('/accounting/statements/balance-sheet', { params: { as_of, compare } })).data
export const getTrialBalanceGaap = async ({ as_of, closing = 'include' }) => (await api.get('/accounting/statements/trial-balance', { params: { as_of, closing } })).data
export const getGeneralLedgerGaap = async ({ start, end, account }) => (await api.get('/accounting/statements/general-ledger', { params: { start, end, ...(account ? { account } : {}) } })).data
export const getSubledger = async (kind, { start, end }) => (await api.get(`/accounting/subledger/${kind === 'ap' ? 'vendors' : 'customers'}`, { params: { start, end, detail: 1 } })).data
export const getStatementClasses = async () => (await api.get('/accounting/statements/classification')).data
export const setStatementClass = async (code, reportClass) => (await api.put(`/accounting/statements/classification/${encodeURIComponent(code)}`, { report_class: reportClass || null })).data

// Transaction types: your own kinds of transaction (see backend transaction_types.py).
export const listTransactionTypes = async (activeOnly = false) => (await api.get('/accounting/transaction-types', { params: activeOnly ? { active: 1 } : {} })).data
export const getTransactionType = async (id) => (await api.get(`/accounting/transaction-types/${id}`)).data
export const saveTransactionType = async (id, payload) => (id ? await api.put(`/accounting/transaction-types/${id}`, payload) : await api.post('/accounting/transaction-types', payload)).data
export const setTransactionTypeActive = async (id, active) => (await api.post(`/accounting/transaction-types/${id}/active`, { active })).data
export const deleteTransactionType = async (id) => (await api.delete(`/accounting/transaction-types/${id}`)).data
export const previewTransactionType = async (id, payload) => (await api.post(`/accounting/transaction-types/${id}/preview`, payload)).data
export const recordTransactionType = async (id, payload) => (await api.post(`/accounting/transaction-types/${id}/record`, payload)).data

// Opening balances: what each account held when the company moved to ASYS.
export const getOpeningBalances = async (accountId) => (await api.get('/accounting/opening-balances', { params: accountId ? { account_id: accountId } : {} })).data
/** payload: { entry_date, entries: [{ account_id, dr, cr }] }; dr and cr both 0 removes one. */
export const saveOpeningBalances = async (payload) => (await api.post('/accounting/opening-balances', payload)).data

// Jobs in progress: income as the work is done on long jobs, and the month-end adjustment.
export const getJobsWip = async (asOf) => (await api.get('/accounting/jobs-wip', { params: asOf ? { as_of: asOf } : {} })).data
export const postJobsWip = async (periodYm) => (await api.post('/accounting/jobs-wip/post', { period_ym: periodYm })).data
export const undoJobsWip = async (periodYm) => (await api.post('/accounting/jobs-wip/undo', { period_ym: periodYm })).data

// Production: factory labour and overhead not assigned to a run, cleared at month-end.
export const postProductionOverhead = async (periodYm) => (await api.post('/accounting/production-overhead/post', { period_ym: periodYm })).data
export const undoProductionOverhead = async (periodYm) => (await api.post('/accounting/production-overhead/undo', { period_ym: periodYm })).data
